import React, { useState, useEffect, useRef } from 'react';
import { addVolunteer, getCongregations } from '../services/firebase';
import './Forms.css';

const DAYS = [
  { key: 'thu', label: 'Thu - April 9, 2026',  field: 'thuAvailability' },
  { key: 'fri', label: 'Fri - April 10, 2026', field: 'friAvailability' },
  { key: 'sat', label: 'Sat - April 11, 2026', field: 'satAvailability' },
  { key: 'sun', label: 'Sun - April 12, 2026', field: 'sunAvailability' },
];

const SHIFT_TIMES = ['7-9 AM', '9-11 AM', '11 AM-1 PM', '1-4 PM', '4-6 PM', '6-8 PM'];

// Returns array of checked shifts for checkbox rendering
function toArray(val) {
  if (!val || val === '' || val === 'Not Available') return [];
  if (val === 'Anytime') return [...SHIFT_TIMES];
  if (Array.isArray(val)) return val;
  return [];
}

function getMode(val) {
  if (!val || val === '' || val === 'Not Available') return 'unavailable';
  if (val === 'Anytime') return 'anytime';
  return 'specific';
}

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  congregation: '',
  thuAvailability: '',
  friAvailability: '',
  satAvailability: '',
  sunAvailability: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  maritalStatus: '',
  spouseName: ''
};

function VolunteerForm() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [smsConsent, setSmsConsent] = useState(false);
  const [congregations, setCongregations] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    getCongregations().then(data => {
      const active = data.filter(c => c.active).map(c => c.name).sort();
      setCongregations(active);
    });
  }, []);

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const setDayMode = (field, mode) => {
    setFormData(prev => ({
      ...prev,
      [field]: mode === 'unavailable' ? 'Not Available'
             : mode === 'anytime'     ? 'Anytime'
             : [], // specific — user picks individual shifts
    }));
  };

  const toggleShift = (field, shift) => {
    setFormData(prev => {
      const current = toArray(prev[field]);
      const updated = current.includes(shift)
        ? current.filter(s => s !== shift)
        : [...current, shift];
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.congregation) {
      showError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const hasAvailability = DAYS.some(d => {
      const val = formData[d.field];
      return val && val !== 'Not Available' && (!Array.isArray(val) || val.length > 0);
    });
    if (!hasAvailability) {
      showError('Please indicate your availability for at least one day.');
      setLoading(false);
      return;
    }

    if (!smsConsent) {
      showError('Please agree to the SMS communications consent to continue.');
      setLoading(false);
      return;
    }

    try {
      const result = await addVolunteer({ ...formData, smsConsent: true });
      if (result.success) {
        setSubmitted(true);
        setFormData(EMPTY_FORM);
      } else {
        showError(`Submission failed: ${result.error || 'Please try again. Check that the app has permission to save data.'}`);
      }
    } catch (err) {
      showError(`An error occurred: ${err.message || 'Please try again.'}`);
      console.error('VolunteerForm submit error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="form-container">
        <div className="success-message">
          <h2>Thank You for Registering!</h2>
          <p>Your volunteer information has been submitted successfully.</p>
          <p>Our scheduling team will contact you to confirm your cart witnessing assignment before the event.</p>
          <button onClick={() => setSubmitted(false)} className="btn btn-primary">
            Submit Another Response
          </button>
          <a href="/" className="btn btn-secondary">Return to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>SMPW - Masters Event</h1>
        <h2>Volunteer Registration</h2>
        <p>Thank you for volunteering to assist with cart witnessing during the SMPW Masters Event. Please complete this form by <strong>March 15, 2026</strong>. Our scheduling team will review your availability and assign you to specific shifts. You will be contacted to confirm your assignment.</p>
      </div>

      <form onSubmit={handleSubmit} className="volunteer-form">
        <div className="form-section">
          <h3>Personal Information</h3>

          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input type="tel" id="phone" name="phone" placeholder="706-555-1234" value={formData.phone} onChange={handleChange} required />
            <small>Please include area code</small>
          </div>

          <div className="form-group">
            <label htmlFor="congregation">Congregation *</label>
            <select id="congregation" name="congregation" value={formData.congregation} onChange={handleChange} required>
              <option value="">Select congregation...</option>
              {congregations.map(cong => (
                <option key={cong} value={cong}>{cong}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Availability for Cart Witnessing</h3>
          <p className="instruction">For each day, select your availability. Use <strong>Not Available</strong> or <strong>Anytime</strong> as quick options, or choose specific shift times.</p>

          {DAYS.map(day => {
            const mode = getMode(formData[day.field]);
            const selected = toArray(formData[day.field]);
            return (
              <div key={day.key} className="avail-day-block">
                <div className="avail-day-title">{day.label}</div>
                <div className="avail-quick-row">
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'unavailable' ? ' active-unavail' : ''}`}
                    onClick={() => setDayMode(day.field, 'unavailable')}
                  >
                    Not Available
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'anytime' ? ' active-avail' : ''}`}
                    onClick={() => setDayMode(day.field, 'anytime')}
                  >
                    Anytime
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'specific' ? ' active-specific' : ''}`}
                    onClick={() => setDayMode(day.field, 'specific')}
                  >
                    Select shifts…
                  </button>
                </div>

                {(mode === 'specific' || mode === 'anytime') && (
                  <div className="avail-checkbox-grid">
                    {SHIFT_TIMES.map(shift => (
                      <label key={shift} className="avail-check-item">
                        <input
                          type="checkbox"
                          checked={selected.includes(shift)}
                          onChange={() => {
                            if (mode === 'anytime') {
                              setFormData(prev => ({ ...prev, [day.field]: SHIFT_TIMES.filter(s => s !== shift) }));
                            } else {
                              toggleShift(day.field, shift);
                            }
                          }}
                        />
                        {shift}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="form-section">
          <h3>Optional Information</h3>

          <div className="form-group">
            <label htmlFor="emergencyContactName">Emergency Contact Name</label>
            <input type="text" id="emergencyContactName" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="emergencyContactPhone">Emergency Contact Phone</label>
            <input type="tel" id="emergencyContactPhone" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="maritalStatus">Marital Status</label>
            <select id="maritalStatus" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
              <option value="">Select...</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="spouseName">Family Member Name</label>
            <input type="text" id="spouseName" name="spouseName" placeholder="First Last" value={formData.spouseName} onChange={handleChange} />
            <small>Spouse, sibling, or other family member attending with you</small>
          </div>
        </div>

        <div className="form-section">
          <div className="sms-consent-box">
            <label className="sms-consent-label">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={e => setSmsConsent(e.target.checked)}
              />
              <span>
                I agree to receive SMS text messages from the SMPW Masters Event scheduling
                team for shift coordination and urgent event updates (e.g. weather changes).
                Message frequency varies. Msg &amp; data rates may apply. Reply{' '}
                <strong>STOP</strong> to opt out at any time.{' '}
                <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                {' '}|{' '}
                <a href="/terms" target="_blank" rel="noreferrer">Terms &amp; SMS Terms</a>
              </span>
            </label>
          </div>
        </div>

        {error && (
          <div ref={errorRef} className="error-message" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>
          <a href="/" className="btn btn-secondary">Cancel</a>
        </div>
      </form>
    </div>
  );
}

export default VolunteerForm;
